import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { hrProfileService } from '@/lib/hrProfileService';
import { useEmployeeUser, buildSessionFromRows } from './EmployeeUserContext';
import {
  Loader2, AlertCircle, CheckCircle2, ArrowRight,
  Mail, Phone, RefreshCw, Lock, KeyRound, Eye, EyeOff, Send, User,
} from 'lucide-react';

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
const isPhone = (v: string) => /^[+]?[\d\s\-().]{7,16}$/.test(v.trim());

function normalisePhone(raw: string): string {
  const digits = raw.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) return digits;
  if (digits.startsWith('0')) return `+972${digits.slice(1)}`;
  return `+${digits}`;
}

// ─── OTP Input ──────────────────────────────────────────────────────────────

function OtpInput({ value, onChange, disabled }: {
  value: string[]; onChange: (v: string[]) => void; disabled: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const focus = (i: number) => refs.current[i]?.focus();

  return (
    <div className="flex justify-center gap-2" dir="ltr">
      {value.map((digit, idx) => (
        <input
          key={idx}
          ref={el => { refs.current[idx] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          onChange={e => {
            const d = e.target.value.replace(/\D/g, '').slice(-1);
            const next = [...value];
            next[idx] = d;
            onChange(next);
            if (d && idx < 5) focus(idx + 1);
          }}
          onKeyDown={e => {
            if (e.key === 'Backspace' && !value[idx] && idx > 0) focus(idx - 1);
            if (e.key === 'ArrowLeft' && idx < 5) focus(idx + 1);
            if (e.key === 'ArrowRight' && idx > 0) focus(idx - 1);
          }}
          onPaste={e => {
            e.preventDefault();
            const digits = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
            const next = [...value];
            for (let i = 0; i < 6; i++) next[i] = digits[i] ?? '';
            onChange(next);
            focus(Math.min(digits.length, 5));
          }}
          onFocus={e => e.target.select()}
          className={[
            'w-11 h-14 text-center text-xl font-bold rounded-xl border-2',
            'transition-all duration-150 outline-none',
            'disabled:opacity-50 disabled:cursor-not-allowed',
            digit
              ? 'border-teal-500 bg-teal-50 text-teal-700'
              : 'border-gray-200 bg-white text-gray-900',
            'focus:border-teal-500 focus:ring-2 focus:ring-teal-200',
          ].join(' ')}
        />
      ))}
    </div>
  );
}

// ─── Main ───────────────────────────────────────────────────────────────────

type LoginTab = 'otp' | 'password';
type OtpStep = 'contact' | 'pin';

export function EmployeeLoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { seedSession } = useEmployeeUser();

  const [activeTab, setActiveTab] = useState<LoginTab>('otp');
  const [accessError, setAccessError] = useState<string | null>(null);

  // OTP state
  const [otpStep, setOtpStep] = useState<OtpStep>('contact');
  const [contact, setContact] = useState('');
  const [sendLoading, setSendLoading] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [otpDigits, setOtpDigits] = useState<string[]>(Array(6).fill(''));
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [otpMethod, setOtpMethod] = useState<'sms' | 'whatsapp' | null>(null);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Password state
  const [pwEmail, setPwEmail] = useState('');
  const [pwPassword, setPwPassword] = useState('');
  const [pwShowPass, setPwShowPass] = useState(false);
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);

  // Pre-fill from invite redirect
  useEffect(() => {
    const state = location.state as { phone?: string; inviteToken?: string } | null;
    if (state?.phone) setContact(state.phone);
  }, [location.state]);

  const contactType: 'email' | 'phone' | 'unknown' = isEmail(contact)
    ? 'email' : isPhone(contact) ? 'phone' : 'unknown';
  const displayContact = contactType === 'phone'
    ? normalisePhone(contact) : contact.trim();

  const startCooldown = (secs = 30) => {
    setResendCooldown(secs);
    cooldownRef.current = setInterval(() => {
      setResendCooldown(prev => {
        if (prev <= 1) { clearInterval(cooldownRef.current!); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  // ─── Load employee session after auth ────────────────────────────────────

  async function loadAndRouteSession(): Promise<string | null> {
    const { data: rows, error } = await hrProfileService.getEmployeeSession();
    if (error || !rows.length) {
      return 'הפרטים שלך לא נמצאו במערכת — צור קשר עם המעסיק שלך';
    }
    const session = buildSessionFromRows(rows);
    if (!session) {
      return 'שגיאה בטעינת הפרופיל — נסה שנית';
    }
    seedSession(session);

    const inviteToken = (location.state as { inviteToken?: string } | null)?.inviteToken;
    if (inviteToken) {
      navigate(`/employee/invite/${inviteToken}`, { replace: true });
    } else {
      navigate('/employee/portal', { replace: true });
    }
    return null;
  }

  // ─── OTP send ────────────────────────────────────────────────────────────

  const doSendOtp = async (
    addr: { email?: string; phone?: string },
    method: 'sms' | 'whatsapp' = 'whatsapp',
  ) => {
    if (addr.email) {
      const { error } = await supabase.auth.signInWithOtp({
        email: addr.email, options: { shouldCreateUser: false },
      });
      return error;
    }
    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-otp`;
    const resp = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
      },
      body: JSON.stringify({ action: 'send', phone: addr.phone, method }),
    });
    if (!resp.ok) {
      const body = await resp.json().catch(() => ({}));
      return { message: body.error ?? 'שליחת קוד נכשלה' } as Error;
    }
    return null;
  };

  const handleSendOtp = async (method?: 'sms' | 'whatsapp') => {
    setAccessError(null);
    setSendError(null);
    const val = contact.trim();
    if (!val) { setSendError('נא להזין אימייל או מספר נייד'); return; }
    if (contactType === 'unknown') {
      setSendError('פרמט לא תקין — הזן אימייל או מספר נייד');
      return;
    }
    const selectedMethod = contactType === 'phone' ? (method ?? 'whatsapp') : 'whatsapp';
    setSendLoading(true);
    try {
      const err = await doSendOtp(
        contactType === 'email' ? { email: val } : { phone: normalisePhone(val) },
        selectedMethod,
      );
      if (err) {
        setSendError(
          (err as { message: string }).message.toLowerCase().includes('signups not allowed')
            ? 'כתובת זו אינה רשומה — צור קשר עם המעסיק שלך'
            : (err as { message: string }).message,
        );
        return;
      }
      startCooldown(30);
      setOtpMethod(selectedMethod);
      setOtpDigits(Array(6).fill(''));
      setVerifyError(null);
      setOtpStep('pin');
    } finally {
      setSendLoading(false);
    }
  };

  // ─── OTP verify ──────────────────────────────────────────────────────────

  const handleVerifyOtp = async () => {
    const token = otpDigits.join('');
    setVerifyError(null);
    setAccessError(null);
    if (token.length < 6) { setVerifyError('נא להזין את כל 6 הספרות'); return; }
    setVerifyLoading(true);
    try {
      if (contactType === 'email') {
        const otpResult = await supabase.auth.verifyOtp({ email: displayContact, token, type: 'email' });
        if (otpResult.error) { setVerifyError('הקוד שגוי או שפג תוקפו'); return; }
      } else {
        const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/whatsapp-otp`;
        const resp = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'verify', phone: displayContact, code: token,
            method: otpMethod ?? 'whatsapp',
          }),
        });
        const body = await resp.json().catch(() => ({}));
        if (!resp.ok || !body.success) {
          setVerifyError(body.error ?? 'הקוד שגוי או שפג תוקפו');
          return;
        }
        if (body.token_hash) {
          const authResult = await supabase.auth.verifyOtp({
            token_hash: body.token_hash, type: 'magiclink',
          });
          if (authResult.error) { setVerifyError('אימות הסשן נכשל — נסה שנית'); return; }
        }
      }
      const errMsg = await loadAndRouteSession();
      if (errMsg) {
        setAccessError(errMsg);
        setOtpStep('contact');
      }
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    setVerifyError(null);
    setOtpDigits(Array(6).fill(''));
    setSendLoading(true);
    try {
      const err = await doSendOtp(
        contactType === 'email' ? { email: displayContact } : { phone: displayContact },
        otpMethod ?? 'whatsapp',
      );
      if (err) setVerifyError((err as { message: string }).message);
      else startCooldown(30);
    } finally {
      setSendLoading(false);
    }
  };

  // ─── Password login ──────────────────────────────────────────────────────

  const handlePasswordLogin = async () => {
    setPwError(null);
    setAccessError(null);
    if (!pwEmail.trim()) { setPwError('נא להזין כתובת אימייל'); return; }
    if (!isEmail(pwEmail)) { setPwError('כתובת האימייל אינה תקינה'); return; }
    if (!pwPassword) { setPwError('נא להזין סיסמה'); return; }

    setPwLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: pwEmail.trim(), password: pwPassword,
      });
      if (error) {
        if (error.message.toLowerCase().includes('invalid login') ||
            error.message.toLowerCase().includes('invalid credentials')) {
          setPwError('אימייל או סיסמה שגויים');
        } else {
          setPwError(error.message);
        }
        return;
      }
      const errMsg = await loadAndRouteSession();
      if (errMsg) setAccessError(errMsg);
    } finally {
      setPwLoading(false);
    }
  };

  const switchTab = (tab: LoginTab) => {
    setActiveTab(tab);
    setAccessError(null);
    setSendError(null);
    setVerifyError(null);
    setPwError(null);
    setOtpStep('contact');
    setOtpMethod(null);
  };

  // ─── Render ──────────────────────────────────────────────────────────────

  return (
    <div dir="rtl"
      className="min-h-screen bg-gradient-to-b from-teal-600 via-teal-500 to-teal-400
                 flex flex-col items-center justify-center px-4 py-10"
    >
      {/* Brand */}
      <div className="mb-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 mb-4
                        bg-white/20 backdrop-blur rounded-2xl shadow-lg">
          <User className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-2xl font-bold text-white tracking-tight">פורטל עובדים</h1>
        <p className="mt-1 text-teal-100 text-sm">כניסה מאובטחת לאזור האישי שלך</p>
      </div>

      {/* Card */}
      <div className="w-full max-w-sm bg-white rounded-3xl shadow-2xl overflow-hidden">
        {/* Tabs */}
        <div className="flex border-b border-gray-100">
          <button
            onClick={() => switchTab('otp')}
            className={[
              'flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold',
              'transition-colors focus:outline-none',
              activeTab === 'otp'
                ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
            ].join(' ')}
          >
            <KeyRound className="w-4 h-4" />
            קוד אימות
          </button>
          <button
            onClick={() => switchTab('password')}
            className={[
              'flex-1 flex items-center justify-center gap-2 py-4 text-sm font-semibold',
              'transition-colors focus:outline-none',
              activeTab === 'password'
                ? 'text-teal-600 border-b-2 border-teal-500 bg-teal-50/50'
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50',
            ].join(' ')}
          >
            <Lock className="w-4 h-4" />
            מייל וסיסמה
          </button>
        </div>

        <div className="px-6 pt-6 pb-8 space-y-5">
          {/* Access error */}
          {accessError && (
            <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                            border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{accessError}</span>
            </div>
          )}

          {/* ═══ OTP Tab ═══ */}
          {activeTab === 'otp' && (
            <>
              <div className="flex gap-1.5">
                <div className="flex-1 h-1 rounded-full bg-teal-500" />
                <div className={`flex-1 h-1 rounded-full transition-colors duration-500
                                 ${otpStep === 'pin' ? 'bg-teal-500' : 'bg-gray-100'}`} />
              </div>

              {otpStep === 'contact' && (
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">שלח קוד אימות</h2>
                    <p className="text-sm text-gray-500 mt-0.5">הזן מספר נייד או אימייל</p>
                  </div>

                  {sendError && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                    border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{sendError}</span>
                    </div>
                  )}

                  <div className="space-y-1.5">
                    <label className="block text-sm font-semibold text-gray-700">
                      אימייל או מספר נייד
                    </label>
                    <div className="relative">
                      {contactType === 'email'
                        ? <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500 pointer-events-none" />
                        : <Phone className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      }
                      <input
                        type="text"
                        inputMode="email"
                        autoFocus
                        value={contact}
                        onChange={e => { setContact(e.target.value); setSendError(null); setAccessError(null); }}
                        onKeyDown={e => e.key === 'Enter' && handleSendOtp()}
                        placeholder="050-0000000 / user@example.com"
                        dir="ltr"
                        disabled={sendLoading}
                        className="w-full pr-10 pl-4 py-3.5 rounded-xl border border-gray-200
                                   text-sm bg-white placeholder:text-gray-400 text-gray-900
                                   focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent
                                   disabled:opacity-60 transition"
                      />
                    </div>
                  </div>

                  {contactType === 'phone' ? (
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => handleSendOtp('sms')}
                        disabled={sendLoading || !contact.trim()}
                        className="flex items-center justify-center gap-2 py-3.5 rounded-xl
                                   bg-teal-600 hover:bg-teal-700 active:bg-teal-800
                                   text-white font-semibold text-sm
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone className="w-4 h-4" />}
                        {sendLoading ? 'שולח...' : 'SMS'}
                      </button>
                      <button
                        onClick={() => handleSendOtp('whatsapp')}
                        disabled={sendLoading || !contact.trim()}
                        className="flex items-center justify-center gap-2 py-3.5 rounded-xl
                                   bg-green-600 hover:bg-green-700 active:bg-green-800
                                   text-white font-semibold text-sm
                                   disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        {sendLoading ? 'שולח...' : 'WhatsApp'}
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => handleSendOtp()}
                      disabled={sendLoading || !contact.trim()}
                      className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                                 bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm
                                 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {sendLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                      {sendLoading ? 'שולח...' : 'שלח קוד אימות'}
                    </button>
                  )}
                </>
              )}

              {otpStep === 'pin' && (
                <>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">הזן קוד אימות</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      שלחנו קוד ל-{' '}
                      <span className="font-medium text-gray-700" dir="ltr">{displayContact}</span>
                    </p>
                  </div>

                  <div className="flex items-center gap-2 px-3.5 py-2.5 bg-green-50
                                  border border-green-200 rounded-xl text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4 shrink-0" />
                    <span>הקוד נשלח ב-{otpMethod === 'sms' ? 'SMS' : 'WhatsApp'}</span>
                  </div>

                  {verifyError && (
                    <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                    border-red-200 rounded-xl text-sm text-red-700">
                      <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                      <span>{verifyError}</span>
                    </div>
                  )}

                  <OtpInput value={otpDigits} onChange={setOtpDigits} disabled={verifyLoading} />

                  <button
                    onClick={handleVerifyOtp}
                    disabled={verifyLoading || otpDigits.join('').length < 6}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                               bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                    {verifyLoading ? 'מאמת...' : 'אמת והיכנס'}
                  </button>

                  <div className="relative">
                    <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-100" /></div>
                    <div className="relative flex justify-center"><span className="px-3 bg-white text-xs text-gray-400">לא קיבלת?</span></div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <button onClick={() => { setOtpStep('contact'); setSendError(null); setVerifyError(null); }}
                      className="text-gray-500 hover:text-gray-700 transition-colors">
                      ← שנה פרטים
                    </button>
                    <button onClick={handleResend}
                      disabled={resendCooldown > 0 || sendLoading}
                      className="flex items-center gap-1.5 text-teal-600 hover:text-teal-700
                                 disabled:text-gray-400 disabled:cursor-not-allowed font-medium transition-colors">
                      <RefreshCw className={`w-3.5 h-3.5 ${sendLoading ? 'animate-spin' : ''}`} />
                      {resendCooldown > 0 ? `שלח שוב (${resendCooldown})` : 'שלח קוד חדש'}
                    </button>
                  </div>
                </>
              )}
            </>
          )}

          {/* ═══ Password Tab ═══ */}
          {activeTab === 'password' && (
            <>
              <div>
                <h2 className="text-lg font-bold text-gray-900">כניסה עם סיסמה</h2>
                <p className="text-sm text-gray-500 mt-0.5">הזן אימייל וסיסמה</p>
              </div>

              {pwError && (
                <div className="flex items-start gap-2.5 px-3.5 py-3 bg-red-50 border
                                border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{pwError}</span>
                </div>
              )}

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">אימייל</label>
                <div className="relative">
                  <Mail className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input type="email" autoFocus value={pwEmail}
                    onChange={e => { setPwEmail(e.target.value); setPwError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
                    placeholder="user@example.com" dir="ltr" disabled={pwLoading}
                    className="w-full pr-10 pl-4 py-3.5 rounded-xl border border-gray-200
                               text-sm bg-white placeholder:text-gray-400 text-gray-900
                               focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent
                               disabled:opacity-60 transition"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-semibold text-gray-700">סיסמה</label>
                <div className="relative">
                  <Lock className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  <input type={pwShowPass ? 'text' : 'password'} value={pwPassword}
                    onChange={e => { setPwPassword(e.target.value); setPwError(null); }}
                    onKeyDown={e => e.key === 'Enter' && handlePasswordLogin()}
                    placeholder="••••••••" dir="ltr" disabled={pwLoading}
                    className="w-full pr-10 pl-10 py-3.5 rounded-xl border border-gray-200
                               text-sm bg-white placeholder:text-gray-400 text-gray-900
                               focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent
                               disabled:opacity-60 transition"
                  />
                  <button type="button" onClick={() => setPwShowPass(p => !p)} tabIndex={-1}
                    className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {pwShowPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <button onClick={handlePasswordLogin}
                disabled={pwLoading || !pwEmail.trim() || !pwPassword}
                className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl
                           bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm
                           disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                {pwLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                {pwLoading ? 'מתחבר...' : 'התחבר'}
              </button>

              <button onClick={() => switchTab('otp')}
                className="w-full text-center text-xs text-teal-600 font-medium hover:underline">
                כניסה בקוד אימות
              </button>
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 flex flex-col items-center gap-2">
        <a href="/login"
          className="text-sm text-teal-100 hover:text-white underline underline-offset-2 transition-colors">
          כניסה למערכת הניהול של Axelio
        </a>
        <a href="/b2b"
          className="text-sm text-teal-100 hover:text-white underline underline-offset-2 transition-colors">
          כניסה לפורטל B2B
        </a>
      </div>
    </div>
  );
}
